import { Injectable } from '@angular/core';
import { Observable, Subscriber, BehaviorSubject } from 'rxjs';
import { filter, take, timeout, catchError, switchMap } from 'rxjs/operators';
import * as uuid from 'uuid';
import { DataFlattenerWorkerModel } from '@models/data-flattener-worker.model';

@Injectable({
  providedIn: 'root',
})
export class DataFlattenerWorkerService {
  private worker: Worker | undefined;
  private workerReady$ = new BehaviorSubject<boolean>(false);
  private readonly WORKER_READY_TIMEOUT = 20000; // 20 seconds

  constructor() {
    if (typeof Worker !== 'undefined') {
      this.worker = new Worker(new URL('../../workers/data-flattener.worker', import.meta.url), {
        type: 'module',
      });
      this.initializeWorker();
    }
  }

  /**
   * Initialize worker and check readiness
   */
  private initializeWorker(): void {
    if (!this.worker) return;

    const pingRequestId = uuid.v4();
    console.log(`[WORKER-SERVICE]: Pinging worker with requestId: ${pingRequestId} at ${new Date().toISOString()}`);

    const handleReadyMessage = (evt: MessageEvent<DataFlattenerWorkerModel.FlattenWorkerMessage>) => {
      const msg = evt?.data;
      
      if (msg.requestId !== pingRequestId || !DataFlattenerWorkerModel.isWorkerReadyMessage(msg)) {
        return;
      }

      console.log(`[WORKER-SERVICE]: Worker ready confirmed for requestId: ${pingRequestId} at ${new Date().toISOString()}`);
      this.worker?.removeEventListener('message', handleReadyMessage);
      this.workerReady$.next(true);
    };

    this.worker.addEventListener('message', handleReadyMessage);

    const pingMessage: DataFlattenerWorkerModel.WorkerPingMessage = {
      type: 'WORKER_PING',
      requestId: pingRequestId,
    };

    this.worker.postMessage(pingMessage);

    // Set a timeout for worker initialization
    setTimeout(() => {
      if (!this.workerReady$.value) {
        console.warn(`[WORKER-SERVICE]: Worker failed to respond within ${this.WORKER_READY_TIMEOUT}ms, assuming ready anyway`);
        this.worker?.removeEventListener('message', handleReadyMessage);
        this.workerReady$.next(true);
      }
    }, this.WORKER_READY_TIMEOUT);
  }

  /**
   * Wait for the worker to be ready. Use this before calling runFlattenWorker
   */
  waitForWorkerReady(): Observable<{ isReady: boolean; reason: 'worker_ready' | 'timeout_fallback' }> {
    const startTime = Date.now();
    console.log(`[WORKER-SERVICE]: Waiting for worker readiness at ${new Date().toISOString()}`);
    
    return this.workerReady$.pipe(
      filter(ready => ready === true),
      take(1),
      timeout(this.WORKER_READY_TIMEOUT + 1000),
      switchMap(() => {
        const waitTime = Date.now() - startTime;
        console.log(`[WORKER-SERVICE]: ✅ Worker confirmed ready after ${waitTime}ms at ${new Date().toISOString()}`);
        return [{ isReady: true, reason: 'worker_ready' as const }];
      }),
      catchError((err) => {
        const waitTime = Date.now() - startTime;
        console.warn(`[WORKER-SERVICE]: ⏰ Worker readiness TIMEOUT after ${waitTime}ms, proceeding anyway at ${new Date().toISOString()}`);
        return [{ isReady: true, reason: 'timeout_fallback' as const }];
      })
    );
  }

  /**
   * Check if worker is ready synchronously
   */
  get isWorkerReady(): boolean {
    return this.workerReady$.value;
  }

  runFlattenWorker(
    data: DataFlattenerWorkerModel.FlattenData,
    fallback: () => DataFlattenerWorkerModel.RunFlattenSuccessMessage
  ): Observable<DataFlattenerWorkerModel.RunFlattenSuccessMessage> {
    return new Observable((subscriber: Subscriber<DataFlattenerWorkerModel.RunFlattenSuccessMessage>) => {
      const requestId = uuid.v4();
      console.log(`processing requestId: ${requestId} at ${new Date().toISOString()}`);
      if (!this.worker) {
        try {
          const fallbackResult = fallback();
          subscriber.next(fallbackResult);
          subscriber.complete();
        } catch (err) {
          subscriber.error(err);
        }
        return undefined;
      }


      const handleMessage = (evt: MessageEvent<DataFlattenerWorkerModel.FlattenWorkerMessage>) => {
        const msg = evt?.data;

        if (msg.requestId !== requestId) {
          console.log(`skipping requestId: ${msg.requestId} at ${new Date().toISOString()}`);
          return;
        };

        this.worker?.removeEventListener('message', handleMessage);

        clearTimeout(fallbackTimeout);

        if (DataFlattenerWorkerModel.isRunFlattenSuccessMessage(msg)) {
          console.log(`observable COMPLETED for requestId: ${msg.requestId} ||| ${requestId} at ${new Date().toISOString()}`);
          subscriber.next(msg);
          subscriber.complete();
        } else if (DataFlattenerWorkerModel.isRunFlattenFailMessage(msg)) {
          console.log(`observable FAILED for requestId: ${msg.requestId} ||| ${requestId} at ${new Date().toISOString()}`);
          subscriber.error(msg.error);
        }
      };

      const fallbackTimeout = setTimeout(() => {

        console.log(`TIMEOUT happned for requestId: ${requestId} at ${new Date().toISOString()}, locations: ${data.locations?.length}, events: ${data.events?.length}, walls: ${data.walls?.length}`);
        this.worker?.removeEventListener('message', handleMessage);

        const runFlattenCancelmessage: DataFlattenerWorkerModel.RunFlattenCancelMessage = {
          type: 'RUN_CANCEL_FLATTEN',
          requestId,
        };

        this.worker?.postMessage(runFlattenCancelmessage);

        try {
          const fallbackResult = fallback();
          subscriber.next(fallbackResult);
          subscriber.complete();
        } catch (err) {
          console.log(`TIMEOUT FALLBACK FAILED for requestId: ${requestId} at ${new Date().toISOString()}`);
          subscriber.error(err);
        }
      }, 5000);

      this.worker.addEventListener('message', handleMessage);

      const message: DataFlattenerWorkerModel.RunFlattenMessage = {
        type: 'RUN_FLATTEN',
        requestId,
        ...data,
      };

      console.log(`[MAIN-THREAD]: publishd 'RUN_FLATTEN' for requestId: ${requestId} at ${new Date().toISOString()}`);

      this.worker.postMessage(message);

      // Cleanup on unsubscribe
      return () => {
        console.log(`cleaning up requestId: ${requestId} at ${new Date().toISOString()}`);
        clearTimeout(fallbackTimeout);
        this.worker?.removeEventListener('message', handleMessage);
      };
    });
  }
}